import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroImages, testimonials, tagline, menuBoardImages, socialLinks, outlets, brandLogo } from '../data/mock';
import { Star, Clock, Shield, TrendingUp, ChevronRight, MapPin, Phone, Navigation } from 'lucide-react';

const Home = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600">
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={heroImages[currentHeroImage]}
            alt="Delicious Punjabi Paratha"
            className="w-full h-full object-cover transition-opacity duration-1000"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        </div>

        {/* Hero Content */}
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-7xl font-black leading-tight mb-4" style={{ textShadow: '3px 3px 6px rgba(0,0,0,0.9), 2px 2px 4px rgba(0,0,0,0.8)' }}>
              <span className="text-yellow-400">LOADED</span><br />
              <span className="text-white">PARATHAS</span><br />
              <span className="text-yellow-500">REAL PUNJABI</span><br />
              <span className="text-white">TASTE</span>
            </h1>
            <p className="text-lg md:text-xl font-bold mb-3 text-yellow-300" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              Butter. Stuffing. Satisfaction.
            </p>
            <p className="text-base md:text-lg font-bold mb-4 text-yellow-200 italic bg-black/40 inline-block px-4 py-2 rounded-lg">
              "{tagline}"
            </p>
            <div className="flex flex-col gap-4">
              <a href="https://wa.me/919601382424" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="text-lg font-bold bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-6 w-full sm:w-auto">
                  Order Now
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href={socialLinks.zomato} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="text-lg font-bold bg-red-600 hover:bg-red-700 text-white px-8 py-6 w-full sm:w-auto">
                  Order on Zomato
                </Button>
              </a>
              <Link to="/franchise">
                <Button size="lg" variant="outline" className="text-lg font-bold border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black px-8 py-6 w-full sm:w-auto">
                  Open a Franchise
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Image Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeroImage(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                currentHeroImage === index ? 'bg-yellow-500 w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Serving Details */}
      <section className="py-8 bg-gradient-to-r from-yellow-500 to-yellow-400">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl md:text-2xl font-black text-black bg-white/20 backdrop-blur-sm px-8 py-4 rounded-xl border-4 border-black inline-block uppercase">
              WE SERVE ALL PARATHAS WITH DAL MAKHANI, CURD, RED GARLIC CHATNI, GREEN CHATNI, PICKLE AND BUTTER.
            </p>
          </div>
        </div>
      </section>

      {/* Menu Boards Section */}
      <section className="py-16 bg-gradient-to-b from-yellow-500 to-yellow-400">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              CHECK OUR <span className="text-black">MENU</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {menuBoardImages.map((image, index) => (
              <Card key={index} className="border-4 border-black overflow-hidden hover:shadow-2xl transition-all bg-black">
                <div className="relative overflow-hidden">
                  <img
                    src={image}
                    alt={`Menu Board ${index + 1}`}
                    className="w-full h-full object-contain hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/menu">
              <Button size="lg" className="text-lg font-bold bg-black hover:bg-gray-900 text-yellow-400 px-8 py-6">
                View Full Menu
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* All Branches Section */}
      <section className="py-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              VISIT OUR <span className="text-black">OUTLETS</span>
            </h2>
            <p className="text-xl text-white font-medium">6 locations across Ahmedabad</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {outlets.map((outlet) => (
              <Card key={outlet.id} className="border-4 border-black hover:shadow-2xl transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-black mb-4 text-black">{outlet.name}</h3>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start space-x-2">
                      <MapPin className="w-5 h-5 text-black mt-1 flex-shrink-0" />
                      <p className="text-gray-800 font-medium text-sm">{outlet.address}</p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-black flex-shrink-0" />
                      <a href={`tel:${outlet.phone}`} className="text-gray-800 font-bold hover:text-black transition-colors">
                        {outlet.phone}
                      </a>
                    </div>
                  </div>

                  <a 
                    href={outlet.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full font-bold bg-black hover:bg-gray-900 text-yellow-400">
                      <Navigation className="w-4 h-4 mr-2" />
                      Get Directions
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/locations">
              <Button size="lg" className="text-lg font-bold bg-black hover:bg-gray-900 text-yellow-400 px-8 py-6">
                View Details & Timings
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-b from-yellow-500 to-yellow-600">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-white">
              WHY CHOOSE <span className="text-black">देशी STUFF?</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-4 border-black hover:shadow-2xl transition-shadow bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black mb-2 text-black">Fresh & Fast</h3>
                <p className="text-gray-800 font-medium">Made fresh on order. Quick service. Hot parathas delivered fast.</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-black hover:shadow-2xl transition-shadow bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black mb-2 text-black">Hygienic & Clean</h3>
                <p className="text-gray-800 font-medium">Maintained standards. Quality ingredients. Consistent taste.</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-black hover:shadow-2xl transition-shadow bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black mb-2 text-black">Growing Chain</h3>
                <p className="text-gray-800 font-medium">6 outlets across Ahmedabad. Trusted brand. Expanding rapidly.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-yellow-400">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-white">
              WHAT PEOPLE <span className="text-black">SAY</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.slice(0, 2).map((testimonial) => (
              <Card key={testimonial.id} className="border-4 border-black hover:shadow-xl transition-shadow bg-white">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                    ))}
                  </div>
                  <p className="text-gray-800 mb-4 font-medium italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-black">{testimonial.name}</p>
                    <p className="text-sm text-gray-700 font-medium">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-yellow-400">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">READY TO TASTE REAL PUNJABI?</h2>
          <p className="text-xl md:text-2xl mb-8 font-bold">Order now or visit our nearest outlet!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/919601382424" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="text-lg font-bold bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-6">
                Order on WhatsApp
              </Button>
            </a>
            <Link to="/locations">
              <Button size="lg" variant="outline" className="text-lg font-bold border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black px-8 py-6">
                Find Outlets
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
